import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { hapticsRaw, type HapticsApi } from '../utils/haptics';

interface HapticsPreferenceValue {
  /** Master switch — when false, every method becomes a no-op. */
  enabled: boolean;
}

const HapticsPreferenceContext = createContext<HapticsPreferenceValue>({ enabled: true });

export interface HapticsProviderProps {
  /** Default `true`. Set from a user setting + the system "Reduce Motion"
   *  / "Haptics off" flag, OR-ed however the app likes. */
  enabled?: boolean;
  children: ReactNode;
}

/**
 * Mount near the app root inside `<TamaguiProvider>`. Provides the
 * preference flag that `useHaptics()` reads. Without this provider,
 * the hook defaults to `enabled: true` — so app screens that forgot to
 * wrap still get vibrations on native (and no-ops on web).
 */
export function HapticsProvider({ enabled = true, children }: HapticsProviderProps) {
  const value = useMemo(() => ({ enabled }), [enabled]);
  return (
    <HapticsPreferenceContext.Provider value={value}>
      {children}
    </HapticsPreferenceContext.Provider>
  );
}

const NOOP_HAPTICS: HapticsApi = {
  light: () => undefined,
  medium: () => undefined,
  heavy: () => undefined,
  selection: () => undefined,
  success: () => undefined,
  warning: () => undefined,
  error: () => undefined,
};

/**
 * App-facing haptics surface per ADR-0079. Returns a stable object with
 * seven methods:
 *
 *   - `light()` — primary tap confirmation
 *   - `medium()` — commit (swipe-to-delete, toggle)
 *   - `heavy()` — discouraging signal
 *   - `selection()` — picker / segmented-control value change
 *   - `success()` / `warning()` / `error()` — outcome notifications
 *
 * No-ops when the parent `<HapticsProvider enabled={false}>` (user
 * preference or system flag). Always no-op on web.
 */
export function useHaptics(): HapticsApi {
  const { enabled } = useContext(HapticsPreferenceContext);
  return useMemo<HapticsApi>(
    () => (enabled ? hapticsRaw : NOOP_HAPTICS),
    [enabled],
  );
}
